package com.kaihuoy.components.common

import androidx.compose.runtime.*
import com.kaihuoy.components.common.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

enum class AppToastType { SUCCESS, ERROR, INFO, WARNING }

internal data class ToastState(val id: Int, val message: String, val type: AppToastType)

internal sealed interface AppDialogState {
    data class Confirm(
        val title: String,
        val message: String,
        val confirmText: String,
        val dismissText: String,
        val confirmColor: CSSColorValue,
        val onConfirm: () -> Unit
    ) : AppDialogState

    data class Prompt(
        val title: String,
        val message: String,
        val label: String,
        val value: String,
        val placeholder: String,
        val confirmText: String,
        val dismissText: String,
        val confirmColor: CSSColorValue,
        val inputMode: String,
        val validator: (String) -> String?,
        val onConfirm: (String) -> Unit
    ) : AppDialogState
}

object AppFeedback {
    private var nextToastId = 0

    internal var toast by mutableStateOf<ToastState?>(null)
        private set

    internal var dialog by mutableStateOf<AppDialogState?>(null)
        private set

    fun showSuccess(message: String) = showToast(message, AppToastType.SUCCESS)
    fun showError(message: String) = showToast(message, AppToastType.ERROR)
    fun showInfo(message: String) = showToast(message, AppToastType.INFO)
    fun showWarning(message: String) = showToast(message, AppToastType.WARNING)

    fun confirm(
        title: String,
        message: String = "",
        confirmText: String = "OK",
        dismissText: String = "Peruuta",
        confirmColor: CSSColorValue = KaihuTheme.Primary,
        onConfirm: () -> Unit
    ) {
        dialog = AppDialogState.Confirm(title, message, confirmText, dismissText, confirmColor, onConfirm)
    }

    fun promptNumber(
        title: String,
        message: String = "",
        label: String = "Arvo",
        initialValue: String = "",
        placeholderText: String = "",
        confirmText: String = "Tallenna",
        dismissText: String = "Peruuta",
        confirmColor: CSSColorValue = KaihuTheme.Primary,
        onConfirm: (Long) -> Unit
    ) {
        dialog = AppDialogState.Prompt(
            title = title,
            message = message,
            label = label,
            value = initialValue,
            placeholder = placeholderText,
            confirmText = confirmText,
            dismissText = dismissText,
            confirmColor = confirmColor,
            inputMode = "numeric",
            validator = { value ->
                if (value.trim().toLongOrNull() == null) "Anna arvo numerona." else null
            },
            onConfirm = { value -> onConfirm(value.trim().toLong()) }
        )
    }

    fun dismissToast() {
        toast = null
    }

    fun dismissDialog() {
        dialog = null
    }

    private fun showToast(message: String, type: AppToastType) {
        nextToastId += 1
        toast = ToastState(nextToastId, message, type)
    }
}

@Composable
fun AppFeedbackHost() {
    val toast = AppFeedback.toast
    val dialog = AppFeedback.dialog

    toast?.let { currentToast ->
        LaunchedEffect(currentToast.id) {
            delay(3200)
            if (AppFeedback.toast?.id == currentToast.id) {
                AppFeedback.dismissToast()
            }
        }
    }

    toast?.let { currentToast ->
        Div(attrs = {
            style {
                position(Position.Fixed)
                top(24.px)
                right(24.px)
                property("z-index", "2000")
            }
        }) {
            Div(attrs = {
                style {
                    backgroundColor(KaihuTheme.Surface)
                    color(KaihuTheme.TextPrimary)
                    padding(16.px, 18.px)
                    borderRadius(14.px)
                    property("box-shadow", KaihuTheme.ShadowLg)
                    property("border", "1px solid ${toastBorderColor(currentToast.type)}")
                    width(320.px)
                    maxWidth(90.vw)
                }
            }) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.FlexStart)
                        gap(12.px)
                    }
                }) {
                    Div {
                        Div(attrs = { style { fontWeight("700"); marginBottom(4.px) } }) {
                            Text(toastTitle(currentToast.type))
                        }
                        Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.92.em) } }) {
                            Text(currentToast.message)
                        }
                    }
                    Button(attrs = {
                        onClick { AppFeedback.dismissToast() }
                        style {
                            backgroundColor(Color.transparent)
                            property("border", "none")
                            color(KaihuTheme.TextSecondary)
                            cursor("pointer")
                            fontSize(1.1.em)
                            padding(0.px)
                        }
                    }) { Text("✕") }
                }
            }
        }
    }

    if (dialog != null) {
        Div(attrs = {
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                right(0.px)
                bottom(0.px)
                backgroundColor(rgba(15, 23, 42, 0.55))
                property("backdrop-filter", "blur(4px)")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                padding(20.px)
                property("z-index", "1999")
            }
        }) {
            dialog.let { currentDialog ->
                when (currentDialog) {
                    is AppDialogState.Confirm -> ConfirmDialog(currentDialog)
                    is AppDialogState.Prompt -> PromptDialog(currentDialog)
                }
            }
        }
    }
}

@Composable
private fun ConfirmDialog(dialog: AppDialogState.Confirm) {
    DialogShell(dialog.title, dialog.message) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(12.px) } }) {
            StyledButton(
                dialog.dismissText,
                onClick = { AppFeedback.dismissDialog() },
                backgroundColor = KaihuTheme.Grey
            )
            StyledButton(dialog.confirmText, onClick = {
                AppFeedback.dismissDialog()
                dialog.onConfirm()
            }, backgroundColor = dialog.confirmColor)
        }
    }
}

@Composable
private fun PromptDialog(dialog: AppDialogState.Prompt) {
    var inputValue by remember(dialog) { mutableStateOf(dialog.value) }
    var errorMessage by remember(dialog) { mutableStateOf<String?>(null) }

    DialogShell(dialog.title, dialog.message) {
        Div(attrs = { style { marginBottom(18.px) } }) {
            Label(attrs = { style { display(DisplayStyle.Block); fontWeight("600"); marginBottom(8.px) } }) {
                Text(dialog.label)
            }
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                value(inputValue)
                onInput {
                    inputValue = it.value
                    errorMessage = null
                }
                attr("inputmode", dialog.inputMode)
                placeholder(dialog.placeholder)
                style {
                    width(100.percent)
                    boxSizing("border-box")
                    padding(12.px, 14.px)
                    borderRadius(10.px)
                    property("border", "1px solid ${KaihuTheme.Border}")
                    fontSize(1.em)
                }
            })

            if (!errorMessage.isNullOrBlank()) {
                Div(attrs = { style { color(KaihuTheme.Danger); fontSize(0.86.em); marginTop(8.px) } }) {
                    Text(errorMessage ?: "")
                }
            }
        }

        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(12.px) } }) {
            StyledButton(
                dialog.dismissText,
                onClick = { AppFeedback.dismissDialog() },
                backgroundColor = KaihuTheme.Grey
            )
            StyledButton(dialog.confirmText, onClick = {
                val validationError = dialog.validator(inputValue)
                if (validationError != null) {
                    errorMessage = validationError
                    return@StyledButton
                }

                AppFeedback.dismissDialog()
                dialog.onConfirm(inputValue)
            }, backgroundColor = dialog.confirmColor)
        }
    }
}

@Composable
private fun DialogShell(title: String, message: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            width(460.px)
            maxWidth(96.vw)
            backgroundColor(KaihuTheme.Surface)
            borderRadius(20.px)
            padding(24.px)
            property("border", "1px solid ${KaihuTheme.Border}")
            property("box-shadow", KaihuTheme.ShadowLg)
        }
    }) {
        H3(attrs = { style { marginTop(0.px); marginBottom(8.px); color(KaihuTheme.TextPrimary) } }) {
            Text(title)
        }
        if (message.isNotBlank()) {
            P(attrs = { style { marginTop(0.px); marginBottom(20.px); color(KaihuTheme.TextSecondary) } }) {
                Text(message)
            }
        }
        content()
    }
}

private fun toastTitle(type: AppToastType): String = when (type) {
    AppToastType.SUCCESS -> "Valmis"
    AppToastType.ERROR -> "Virhe"
    AppToastType.INFO -> "Ilmoitus"
    AppToastType.WARNING -> "Huomio"
}

private fun toastBorderColor(type: AppToastType): CSSColorValue = when (type) {
    AppToastType.SUCCESS -> KaihuTheme.Success
    AppToastType.ERROR -> KaihuTheme.Danger
    AppToastType.INFO -> KaihuTheme.Info
    AppToastType.WARNING -> KaihuTheme.Warning
}